import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import { StatCard, SectionHeader } from '../components/DashboardComponents';
import { FaWallet, FaArrowUp, FaArrowDown, FaExchangeAlt, FaHistory } from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const AdminWalletPage = () => {
    const [stats, setStats] = useState({
        volume: 0,
        revenue: 0,
        adminBalance: 0,
        totalRecharges: 0,
        pendingPayouts: 0
    });
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchFinancialData = async () => {
            try {
                setLoading(true);
                const [statsRes, txRes] = await Promise.all([
                    apiClient.get('/admin/stats'),
                    apiClient.get('/admin/transactions')
                ]);
                
                setStats(statsRes.data.finances);
                setTransactions(txRes.data);
                setError('');
            } catch (err) {
                console.error(err);
                setError('Failed to load financial records.');
            } finally {
                setLoading(false);
            }
        };
        fetchFinancialData();
    }, []);

    const formatCurrency = (val) => `${(val || 0).toLocaleString()}`;
    const formatCurrencySafe = (val) => `${(val || 0).toLocaleString()}`;

    const handleExportCSV = () => {
        if (transactions.length === 0) return alert("No data to export.");

        const headers = ["Transaction ID", "Description", "Type", "User", "Date", "Amount", "Status"];
        const rows = transactions.map(tx => [
            tx.id,
            `"${tx.description}"`,
            tx.type,
            tx.user,
            new Date(tx.date).toLocaleDateString(),
            tx.amount,
            tx.status
        ]);

        const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Service24_Financials_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleGenerateReport = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const dateStr = new Date().toLocaleString();

        // Header
        doc.setFontSize(22);
        doc.setTextColor(5, 150, 105); // emerald-600
        doc.text("SERVICE24", 14, 20);
        
        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text("PLATFORM FINANCIAL REPORT", 14, 28);
        doc.text(`Generated on: ${dateStr}`, pageWidth - 14, 28, { align: 'right' });
        
        doc.setDrawColor(230);
        doc.line(14, 32, pageWidth - 14, 32);

        // Summary Section
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text("Financial Summary", 14, 45);

        const summaryData = [
            ["Metric", "Value"],
            ["Total Platform Volume", formatCurrencySafe(stats.volume)],
            ["Net Platform Revenue", formatCurrencySafe(stats.revenue)],
            ["Admin Wallet Balance", formatCurrencySafe(stats.adminBalance)],
            ["Total Transactions", transactions.length.toString()]
        ];

        autoTable(doc, {
            startY: 50,
            head: [summaryData[0]],
            body: summaryData.slice(1),
            theme: 'striped',
            headStyles: { fillColor: [51, 65, 85] }, // slate-700
        });

        // Transactions Table
        doc.setFontSize(14);
        doc.text("Detailed Transaction Log", 14, doc.lastAutoTable.finalY + 15);

        const tableHeaders = [["ID", "Description", "Type", "User", "Date", "Amount", "Status"]];
        const tableData = transactions.map(tx => [
            tx.id.slice(0, 8),
            tx.description,
            tx.type,
            tx.user,
            new Date(tx.date).toLocaleDateString(),
            formatCurrencySafe(tx.amount),
            tx.status
        ]);

        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: tableHeaders,
            body: tableData,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [5, 150, 105] }, // emerald-600
        });

        doc.save(`Service24_Full_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-64 font-sans">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800 mb-4"></div>
            <p className="font-bold text-slate-500">Accessing Secure Vault...</p>
        </div>
    );

    if (error) return <div className="p-8 text-center text-red-600 font-bold bg-red-50 rounded-2xl">{error}</div>;

    return (
        <div className="space-y-8 animate-fade-in font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h1 className="text-3xl font-black text-slate-800 tracking-tight flex items-center gap-3">
                    <FaWallet className="text-red-600" /> Platform Financials
                </h1>
                <div className="flex gap-2">
                    <button 
                        onClick={handleExportCSV}
                        className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 shadow-lg transition-all text-sm active:scale-95"
                    >
                        Export CSV
                    </button>
                    <button 
                        onClick={handleGenerateReport}
                        className="bg-red-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-red-700 shadow-lg shadow-red-200 transition-all text-sm active:scale-95"
                    >
                        Generate Report
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <StatCard title="Total Platform Volume" value={formatCurrency(stats.volume)} icon={FaExchangeAlt} color="blue" />
                <StatCard title="Net Platform Revenue" value={formatCurrency(stats.revenue)} icon={FaArrowUp} color="green" />
                <StatCard title="Admin Wallet Balance" value={formatCurrency(stats.adminBalance)} icon={FaWallet} color="emerald" />
                <StatCard title="Total Transactions" value={transactions.length} icon={FaHistory} color="purple" />
            </div>

            {/* Transaction Log */}
            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <SectionHeader title="Global Transaction Log" />
                    <span className="text-xs font-black text-slate-400 uppercase tracking-widest bg-white px-3 py-1 rounded-full border border-slate-100 shadow-sm">
                        Live Data
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50 text-slate-400 text-[10px] font-black uppercase tracking-widest">
                                <th className="px-8 py-4">Transaction Details</th>
                                <th className="px-8 py-4">Type</th>
                                <th className="px-8 py-4">User</th>
                                <th className="px-8 py-4">Date</th>
                                <th className="px-8 py-4 text-right">Amount</th>
                                <th className="px-8 py-4">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {transactions.length > 0 ? transactions.map(tx => (
                                <tr key={tx.id} className="hover:bg-slate-50 transition-colors group">
                                    <td className="px-8 py-5">
                                        <div className="text-sm font-black text-slate-800">#TX-{tx.id.slice(0,8)}</div>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase truncate max-w-[150px]">{tx.description}</p>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase ${
                                            tx.type === 'RECHARGE' || tx.type === 'CREDIT' ? 'bg-blue-100 text-blue-700' :
                                            tx.type === 'FEES' ? 'bg-green-100 text-green-700' :
                                            'bg-orange-100 text-orange-700'
                                        }`}>
                                            {tx.type}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-sm font-bold text-slate-700">{tx.user}</td>
                                    <td className="px-8 py-5 text-sm text-slate-500 font-medium">
                                        {new Date(tx.date).toLocaleDateString()}
                                    </td>
                                    <td className="px-8 py-5 text-sm font-black text-slate-800 text-right">{formatCurrency(tx.amount)}</td>
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${tx.status === 'COMPLETED' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                                            <span className="text-xs font-bold text-slate-600 capitalize">{tx.status.toLowerCase()}</span>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="6" className="p-20 text-center text-slate-400 font-bold italic">No transactions recorded yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminWalletPage;